/**
 * useBatchPayout — WP-D (Sammel-Auszahlung, echter Batch-Endpunkt)
 *
 * Kontrakt (AUTORITATIV, siehe Auftragstext WP-D):
 *   POST /api/v2/commissions/pay
 *     Body (alle optional): { period_month?: 'YYYY-MM' }
 *     -> 201 BatchPayoutResponse
 *     -> 422 { error: 'invalid_period_month' }
 *   GET /api/v2/commissions/:id
 *     -> 200 BatchPayoutResponse (fuer Payouts mit source === 'settlements';
 *        skipped ist dort immer [] — Skip ist ein Generierungszeitpunkt-
 *        Konzept, siehe Contract-Notiz)
 *   POST /api/v2/commissions/:id/blocks/:block_id/mark_paid
 *     -> 200 BatchPayoutBlock (paid: true)
 *
 * Keiner dieser drei Endpunkte ist im generierten `@heliumtracker/api-types`-
 * Paket enthalten (separates Build/Repo, Contract ist neuer). Aufruf daher
 * — wie useAddHotspot.ts es fuer `/api/v2/hotspots/{id}/watch` vormacht —
 * ueber eine lokale Typ-Assertion auf den bestehenden, generierten
 * `apiClient` (openapi-fetch): die zentrale Auth-/401-Refresh-/Fehler-
 * Middleware in lib/api-client.ts greift unveraendert, da sie auf der HTTP-
 * Response arbeitet, nicht auf dem generierten Pfad-Typ. KEIN roher
 * `fetch()` — das wuerde Auth-Header, Token-Refresh und die zentrale
 * ApiError-Behandlung (F012) umgehen.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '../../lib/api-client'

// --- Typen (lokal, bis der Contract in api-types nachgezogen ist) -----------

export type BatchPayoutPayee = {
  address: string
  host_name: string | null
  amount: number
  amount_bones: number
  memo: string
  settlement_ids: number[]
}

export type BatchPayoutBlock = {
  id: number
  payees: BatchPayoutPayee[]
  qr_payload: string
  qr_payload_count: number
  total_amount: number
  paid: boolean
}

export type BatchPayoutSkipReason = 'no_wallet_address' | 'invalid_address'

export type BatchPayoutSkipped = {
  host_id: number
  host_name: string | null
  reason: BatchPayoutSkipReason
  amount: number
}

export type BatchPayoutResponse = {
  payout_id: number
  blocks: BatchPayoutBlock[]
  skipped: BatchPayoutSkipped[]
}

export type StartBatchPayoutInput = {
  /** 'YYYY-MM' — undefined = alle faelligen Monate. */
  period_month?: string
}

// --- Untypisierter Zugriff auf den generierten Client -----------------------

type ApiResult<T> = Promise<{ data?: T; error?: unknown }>

type UntypedClient = {
  POST: (
    path: string,
    init?: { body?: unknown; params?: { path?: Record<string, string> } },
  ) => ApiResult<unknown>
  GET: (path: string, init?: { params?: { path?: Record<string, string> } }) => ApiResult<unknown>
}

function untyped(): UntypedClient {
  return apiClient as unknown as UntypedClient
}

async function startBatchPayout(input: StartBatchPayoutInput): Promise<BatchPayoutResponse> {
  const result = await untyped().POST('/api/v2/commissions/pay', {
    body: input.period_month ? { period_month: input.period_month } : {},
  })
  if (result.error) throw result.error
  return result.data as BatchPayoutResponse
}

async function fetchBatchPayout(payoutId: number): Promise<BatchPayoutResponse> {
  const result = await untyped().GET('/api/v2/commissions/{id}', {
    params: { path: { id: String(payoutId) } },
  })
  if (result.error) throw result.error
  return result.data as BatchPayoutResponse
}

async function markBlockPaid(payoutId: number, blockId: number): Promise<BatchPayoutBlock> {
  const result = await untyped().POST('/api/v2/commissions/{id}/blocks/{block_id}/mark_paid', {
    params: { path: { id: String(payoutId), block_id: String(blockId) } },
  })
  if (result.error) throw result.error
  return result.data as BatchPayoutBlock
}

// --- Query-Keys ---------------------------------------------------------------

const detailKey = (payoutId: number | undefined) => ['commissions', 'batch-payout', payoutId]

// --- Hooks ---------------------------------------------------------------------

/** POST /api/v2/commissions/pay — startet einen neuen Sammel-Auszahlungslauf. */
export function useStartBatchPayout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: StartBatchPayoutInput) => startBatchPayout(input),
    onSuccess: (data) => {
      // Antwort direkt als Detail-Query-Ergebnis cachen — vermeidet einen
      // sofortigen zweiten GET-Roundtrip, sobald die Seite auf payoutId in
      // der URL umschaltet (Wiederaufnahme-Mechanismus, siehe unten).
      queryClient.setQueryData(detailKey(data.payout_id), data)
    },
  })
}

/**
 * GET /api/v2/commissions/:id — laedt einen bestehenden Payout (Grundlage
 * der Wiederaufnahme: die Seite merkt sich nur `payoutId` in der URL-Query,
 * ein Reload laedt den kompletten Stand hierueber neu statt ihn zu verlieren).
 */
export function useBatchPayoutDetail(payoutId: number | undefined) {
  return useQuery({
    queryKey: detailKey(payoutId),
    queryFn: () => fetchBatchPayout(payoutId as number),
    enabled: payoutId !== undefined && Number.isFinite(payoutId),
  })
}

/** POST .../blocks/:block_id/mark_paid — markiert einen Block als bezahlt. */
export function useMarkBlockPaid(payoutId: number | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (blockId: number) => markBlockPaid(payoutId as number, blockId),
    onSuccess: (updatedBlock) => {
      if (payoutId === undefined) return
      // Optimistischer Cache-Merge statt Refetch: der Contract liefert nur
      // den EINEN aktualisierten Block zurueck, nicht die ganze Liste.
      queryClient.setQueryData<BatchPayoutResponse | undefined>(detailKey(payoutId), (prev) =>
        prev
          ? {
              ...prev,
              blocks: prev.blocks.map((b) => (b.id === updatedBlock.id ? updatedBlock : b)),
            }
          : prev,
      )
      // Die zugehoerigen Settlements wechseln serverseitig auf 'settled' —
      // Settlements-Liste/Uebersicht muss das nachziehen.
      void queryClient.invalidateQueries({ queryKey: ['settlements'] })
    },
  })
}
